<template>
    <div data-test-id="l-reviews-edit-basics-template-form">
        <!-- Basics -->
        <h4 class="reviewSetupHeading color-dark">
            <strong>{{ $t(`reviews.admin.templateBasic`) }}</strong>
        </h4>

        <l-input-text
            v-model="template.name"
            type="text"
            :label="$t(`reviews.admin.labelTemplateName`)"
            :placeholder="$t(`reviews.admin.placeholderTitle`)"
        />

        <l-space :size="1" />

        <l-input-text
            v-model="template.description"
            type="textarea"
            :rows="3"
            :label="$t(`reviews.admin.labelTemplateDescription`)"
            :placeholder="$t(`reviews.admin.placeholderDescription`)"
            optional
        />
        <!--/Basics -->

        <!-- Owners -->
        <h4 class="reviewSetupHeading color-dark">
            <strong>{{ $t(`reviews.admin.templateOwners`) }}</strong>
        </h4>

        <div v-for="(owner, index) in template.owners" :key="owner._id" class="card card-light">
            <div class="card-header">
                <div class="row">
                    <div class="col-md-11">{{ owner.firstname }} {{ owner.lastname }}</div>
                    <div class="col-md-1">
                        <span
                            v-show="user._id !== owner._id"
                            class="pull-right color-grey clickable"
                            tabindex="0"
                            @click="removeOwner(index)"
                            @keyup.enter="removeOwner(index)"
                        >
                            <i class="fal fa-user-minus color-primary"></i>
                        </span>
                    </div>
                </div>
            </div>
        </div>

        <l-space :size="1" />

        <l-input-typeahead
            :label="``"
            :placeholder="$t(`reviews.admin.placeholderOwners`)"
            :data="users"
            :on-select="addOwner"
            :on-input-change="onTypeaheadUserInputChange"
        />
        <!-- /Owners -->

        <!-- Access -->
        <h4 class="reviewSetupHeading color-dark">
            <strong>{{ $t(`reviews.admin.templateCanCopy`) }}</strong>
        </h4>

        <label class="color-dark">
            <input type="checkbox" checked disabled />
            <span class="l-default-margin-s">{{ $t(`reviews.admin.templateForAdmins`) }}</span>
        </label>
        <br />
        <label class="color-dark">
            <input v-model="template.templateForManagers" type="checkbox" />
            <span class="l-default-margin-s">{{ $t(`reviews.admin.templateForManagers`) }}</span>
        </label>
        <br />
        <label class="color-dark">
            <input v-model="template.templateForEmployees" type="checkbox" />
            <span class="l-default-margin-s">{{ $t(`reviews.admin.templateForEmployees`) }}</span>
        </label>
        <!--/Access -->

        <!-- Error -->
        <div v-if="error" id="error" class="alert alert-danger">
            <span>{{ error.message }}</span>
        </div>
        <!--/Error -->

        <hr />

        <div class="row">
            <div class="col-md-4 col-md-offset-8">
                <button
                    class="btn btn-primary btn-block btn-raised"
                    type="button"
                    translate=""
                    @click="next()"
                >
                    {{ $t(`reviews.admin.btnContinue`) }}
                </button>
            </div>
        </div>
    </div>
</template>

<script>
import { mapGetters } from "vuex";
import debounce from "lodash.debounce";

import format from "@/utils/format-typeahead";

import LInputText from "@/components/LInputText";
import LInputTypeahead from "@/components/LInputTypeahead";
import LSpace from "@/components/LSpace";

export default {
    components: {
        LInputText,
        LInputTypeahead,
        LSpace,
    },
    props: {
        id: {
            type: String,
            required: true,
        },
    },
    data() {
        return {
            template: {
                name: "",
                description: "",
                owners: [],
                templateForManagers: false,
                templateForEmployees: false,
            },
            error: null,
        };
    },
    computed: {
        ...mapGetters({
            user: "users/current/user",
            getTemplateDetails: "reviews/templates/details",

            usersSearchList: "users/search/list",
        }),

        users() {
            const ownersIds = (this.template.owners || []).map(({ _id }) => _id);

            return (
                this.usersSearchList
                    // Filter current owners
                    .filter(user => !ownersIds.includes(user._id))
                    // Format
                    .map(format.users)
            );
        },
    },
    watch: {
        $route: {
            immediate: true,
            handler() {
                this.template = this.getTemplateDetails(this.id);
            },
        },
        template: {
            deep: true,
            handler: debounce(function handler(template) {
                // Don't auto-save when creating a new template as the operation is not idempotant,
                // and thus can lead to duplicate templates being created on the backend.
                if (template.isNew) {
                    return;
                }

                this.update(template);
            }, 1000),
        },
    },
    methods: {
        onTypeaheadUserInputChange(query) {
            if (query.length >= 2) {
                this.$store.dispatch({ type: "users/search/findByName", query });
            } else {
                this.$store.dispatch({ type: "users/search/reset" });
            }
        },
        addOwner(owner) {
            if (this.template.owners.every(user => user._id !== owner._id)) {
                this.template.owners.push({
                    _id: owner._id,
                    firstname: owner.firstname,
                    lastname: owner.lastname,
                });
            }

            this.$store.dispatch({ type: "users/search/reset" });
        },
        removeOwner(index) {
            this.template.owners.splice(index, 1);
        },

        async update(template) {
            this.error = null;

            try {
                await this.$store.dispatch({
                    type: "reviews/templates/update",
                    payload: { template, scope: "basics" },
                });
            } catch (err) {
                if (err?.response?.data?.message) {
                    this.error = { message: err?.response?.data?.message };
                }
            }
        },

        async next() {
            await this.update(this.template);

            if (this.error === null) {
                this.$emit("next");
            }
        },
    },
};
</script>
